import React, {
	type ComponentType,
	forwardRef,
	type ForwardRefExoticComponent,
	Fragment,
	useCallback,
	useState,
} from 'react';

import { cssMap } from '@atlaskit/css';
import Popup from '@atlaskit/popup';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip, { type TooltipProps } from '@atlaskit/tooltip';

import HoverPopover from './HoverPopover';

export type MinimumItemData = {
	isColored?: boolean;
	key: string | number;
};

export type Props<TData extends MinimumItemData> = {
	initialIsOpen?: boolean;
	isHoverPopoverEnabled?: boolean;
	ItemComponent: ComponentType<Omit<TData, 'key'>>;
	items: TData[];
	maxLimit?: number;
	OverrideContentComponent?: React.JSX.Element;
	PopupContentWrapper?:
		| ComponentType<{
				[key: string]: unknown;
				children: React.ReactNode;
		  }>
		| ComponentType<{
				[key: string]: unknown;
		  }>
		| ForwardRefExoticComponent<{
				[key: string]: unknown;
		  }>;
	showMoreTooltip: string;
};

const ShowMorePopup = <TData extends MinimumItemData>({
	showMoreTooltip,
	items,
	ItemComponent,
	PopupContentWrapper = Fragment,
	maxLimit = 99,
	OverrideContentComponent = undefined,
	initialIsOpen = false,
	isHoverPopoverEnabled = false,
}: Props<TData>): React.JSX.Element | null => {
	const [isOpen, setIsOpen] = useState(initialIsOpen);
	const list = items.slice(0, maxLimit);

	const content = useCallback(
		() =>
			list.map(({ key, ...item }) => (
				<Fragment key={key}>
					<ItemComponent {...(item as Omit<TData, 'key'>)} />
				</Fragment>
			)),
		[ItemComponent, list],
	);

	if (items.length === 0) {
		return null;
	}

	const onClickTrigger = () => {
		setIsOpen(!isOpen);
	};

	const contentWithWrapper = () => (
		<Box testId="list-with-popup.ui.show-more-popup.content" xcss={styles.contentWrapper}>
			{OverrideContentComponent === undefined ? (
				<PopupContentWrapper>{content()}</PopupContentWrapper>
			) : (
				OverrideContentComponent
			)}
		</Box>
	);

	if (isHoverPopoverEnabled) {
		const hoverContent = (
			<Box testId="list-with-popup.ui.show-more-popup.content" xcss={styles.contentWrapper}>
				{OverrideContentComponent === undefined ? (
					<PopupContentWrapper>{content()}</PopupContentWrapper>
				) : (
					OverrideContentComponent
				)}
			</Box>
		);
		return (
			<HoverPopover content={hoverContent} placement="bottom">
				{(triggerProps) => (
					<Pressable
						testId="list-with-popup.ui.show-more-popup.items-count"
						onClick={onClickTrigger}
						{...triggerProps}
						xcss={styles.itemsCountButton}
					>
						+{list.length}
					</Pressable>
				)}
			</HoverPopover>
		);
	}

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom"
			content={contentWithWrapper}
			trigger={(triggerProps) => (
				<Tooltip
					key="triggerLinksDialog"
					content={showMoreTooltip}
					tag={TooltipContainer}
					position="top"
				>
					<Pressable
						{...triggerProps}
						onClick={onClickTrigger}
						aria-expanded={isOpen}
						xcss={styles.itemsCountButton}
						onKeyDown={(e) => {
							if (e.key === ' ' || e.key === 'Enter') {
								e.preventDefault();
								onClickTrigger();
							}
							if (e.key === 'Escape') {
								e.preventDefault();
								setIsOpen(false);
							}
						}}
						testId="list-with-popup.ui.show-more-popup.items-count"
					>
						+{list.length}
					</Pressable>
				</Tooltip>
			)}
		/>
	);
};

const TooltipContainer: TooltipProps['tag'] = forwardRef((props, ref) => (
	// eslint-disable-next-line react/jsx-props-no-spreading
	<Box {...props} ref={ref} as="span" xcss={styles.tooltipContainer} />
));

const styles = cssMap({
	tooltipContainer: {
		maxWidth: '100%',
	},
	itemsCountButton: {
		display: 'inline-flex',
		alignItems: 'center',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.small', '3px'),
		borderColor: token('color.border'),
		paddingInline: token('space.050'),
		color: token('color.text'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	contentWrapper: {
		paddingInline: token('space.200'),
		paddingBlock: token('space.200'),
	},
});

export default ShowMorePopup;
